import * as am5 from '@amcharts/amcharts5';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5xy from '@amcharts/amcharts5/xy';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  InputSignal,
  OnDestroy,
  PLATFORM_ID,
  Signal,
  computed,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { Subject } from 'rxjs';
import { OverlayDirective } from '../overlay/overlay.directive';
import { ToggleComponent } from '../toggle/toggle.component';
import {
  AmChartLine,
  AmLineChartItem,
  TimelineChartInput,
  TimelineChartSection,
} from './models/timeline-chart-input.interface';
import { TimelineChartService } from './service/timeline-chart.service';

/**
 * Liveness probe for the deprecated v1 chart — see
 * `docs/plans/todo-burndown/2026-08-05-b3-timeline-chart.md`.
 *
 * Nothing in this repo can build a v1 (`mainTimeline`) payload, but the wire
 * format may still exist on a backend we cannot exercise, so v1 survives as the
 * `@else` fallback in `google-maps.component.html`. Module-scoped so the warning
 * fires once per page load rather than once per instance.
 */
let hasWarnedAboutV1Usage = false;

@Component({
  selector: 'pozi-timeline-chart',
  imports: [CommonModule, OverlayDirective, ToggleComponent, RouterLink],
  templateUrl: './timeline-chart.component.html',
  styleUrl: './timeline-chart.component.scss',
})
export class TimelineChartComponent implements OnDestroy, AfterViewInit {
  private platformId = inject(PLATFORM_ID);
  private timelineChartService = inject(TimelineChartService);

  data: InputSignal<TimelineChartInput> = input.required<TimelineChartInput>();

  timelineChartInput: Signal<TimelineChartInput> = computed(() => {
    const issecondaryTimelineConvertable =
      this.isTimelineConvertableFromIntervalToRatio(
        this.data().secondaryTimeline
      );
    const secondaryTimelineInMs = issecondaryTimelineConvertable
      ? this.getTimelineIntervalInMs(this.data().secondaryTimeline!)
      : 0;

    const ismainTimelineConvertable =
      this.isTimelineConvertableFromIntervalToRatio(
        this.data().mainTimeline.timelineSections
      );
    const mainTimelineInMs = ismainTimelineConvertable
      ? this.getTimelineIntervalInMs(this.data().mainTimeline.timelineSections)
      : 0;

    return {
      ...this.data(),
      secondaryTimeline: issecondaryTimelineConvertable
        ? this.data().secondaryTimeline?.map(
            (timelineChartSection: TimelineChartSection) =>
              this.convertTimelineIntervalToRatio(
                timelineChartSection,
                secondaryTimelineInMs
              )
          )
        : this.data().secondaryTimeline,
      mainTimeline: ismainTimelineConvertable
        ? {
            ...this.data().mainTimeline,
            gap: this.data().mainTimeline.gap ?? 0,
            timelineSections: this.data().mainTimeline.timelineSections.map(
              (timelineChartSection: TimelineChartSection) =>
                this.convertTimelineIntervalToRatio(
                  timelineChartSection,
                  mainTimelineInMs
                )
            ),
          }
        : this.data().mainTimeline,
    };
  });

  hasExactTimes: Signal<boolean> = computed(() => {
    return this.isTimelineConvertableFromIntervalToRatio(
      this.data().mainTimeline.timelineSections
    );
  });

  /**
   * Series key → whether the series is toggled on.
   *
   * Seeded from `data().amChart.lines` and rewritten — never mutated in place —
   * by {@link toggle}, so the template's reads stay reactive under OnPush.
   */
  amChartToggles = linkedSignal<Map<string, boolean>>(
    () =>
      new Map(
        (this.data().amChart?.lines ?? []).map((line: AmChartLine) => [
          line.key,
          !line.disabled,
        ])
      )
  );

  amChartSeries: Map<string, am5xy.XYSeries> = new Map<
    string,
    am5xy.XYSeries
  >();

  amChartMouseOver = output<AmLineChartItem>();

  cdkOverlayPositionPairs: ConnectedPosition[] = [
    {
      offsetX: 0,
      offsetY: 8,
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      panelClass: 'bottom',
    },
    {
      offsetX: 0,
      offsetY: -8,
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      panelClass: 'top',
    },
    {
      offsetX: 8,
      offsetY: 0,
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      panelClass: 'right',
    },
    {
      offsetX: -8,
      offsetY: 0,
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      panelClass: 'left',
    },
  ];

  private root!: am5.Root;

  private destroy$: Subject<void> = new Subject<void>();

  constructor() {
    if (!hasWarnedAboutV1Usage) {
      hasWarnedAboutV1Usage = true;
      console.warn(
        '[POZI-V1-TIMELINE-PROBE] A legacy TimelineChartInput (`mainTimeline`) payload reached the deprecated TimelineChartComponent. ' +
          'Report this: it is the evidence that blocks deleting timeline-chart v1 (see docs/plans/todo-burndown/2026-08-05-b3-timeline-chart.md). ' +
          'Use TimelineChartV2Component for new work.'
      );
    }
  }

  ngAfterViewInit() {
    this.browserOnly(() => {
      this.createAmChart();
    });
  }

  /**
   * @param timeline
   * @returns true, when all the TimelineChartSection elements have a startTime and an endTime
   */
  isTimelineConvertableFromIntervalToRatio(
    timeline: TimelineChartSection[] | undefined
  ): boolean {
    if (timeline) {
      return !timeline.find(
        (timelineChartSection: TimelineChartSection) =>
          !timelineChartSection.startTime || !timelineChartSection.endTime
      );
    } else {
      return false;
    }
  }

  /**
   * @param allIntervalSum the timeline's total span in ms. A zero span (every
   *   section at the same instant) has no ratio to divide out, so the section is
   *   given the full width rather than a `NaN%` one.
   */
  convertTimelineIntervalToRatio(
    timelineChartSection: TimelineChartSection,
    allIntervalSum: number
  ): TimelineChartSection {
    return {
      ...timelineChartSection,
      ratio: allIntervalSum
        ? (this.getIntervalInMs(
            timelineChartSection.startTime!,
            timelineChartSection.endTime!
          ) /
            allIntervalSum) *
          100
        : 100,
    };
  }

  getTimelineIntervalInMs(
    timelineChartSection: TimelineChartSection[]
  ): number {
    return this.getIntervalInMs(
      timelineChartSection.at(0)!.startTime!,
      timelineChartSection.at(timelineChartSection.length - 1)!.endTime!
    );
  }

  getIntervalInMs(start: string | Date, end: string | Date): number {
    const startDate = start instanceof Date ? start : new Date(start);
    const endDate = end instanceof Date ? end : new Date(end);
    return endDate.getTime() - startDate.getTime();
  }

  readonly timeLabels = computed<string[]>(() => {
    return this.getHoursStringBetweenTimes(
      this.data().startingTime,
      this.data().endingTime
    );
  });

  readonly exactTimeLabels = computed<(Date | undefined)[]>(() => {
    const timelineSections = this.data().mainTimeline.timelineSections;
    const firstIntervalStart = new Date(timelineSections.at(0)!.startTime!);
    const lastIntervalEnd = new Date(
      timelineSections.at(timelineSections.length - 1)!.endTime!
    );
    return [
      firstIntervalStart,
      ...this.getHourlyDates(firstIntervalStart!, lastIntervalEnd!),
      lastIntervalEnd,
    ];
  });

  getHourlyDates(start: Date, end: Date): Date[] {
    const dates: Date[] = [];
    const currentDate = new Date(start);

    // Ensure the minutes and seconds are set to 0
    currentDate.setMinutes(0, 0, 0);
    currentDate.setHours(currentDate.getHours() + 1);

    while (currentDate <= end) {
      dates.push(new Date(currentDate));
      currentDate.setHours(currentDate.getHours() + 1);
    }

    // Exclude the first date if it's within 15 minutes of the start date
    if (dates.length > 1 && this.isLessThan15Minutes(dates.at(0)!, start)) {
      dates.shift();
    }

    // Exclude the last date if it's within 15 minutes of the end date
    if (
      dates.length > 1 &&
      this.isLessThan15Minutes(end, dates[dates.length - 1])
    ) {
      dates.pop();
    }

    return dates;
  }

  isLessThan15Minutes(date1: Date, date2: Date): boolean {
    const diffInMilliseconds = Math.abs(date2.getTime() - date1.getTime());
    const diffInMinutes = diffInMilliseconds / (1000 * 60);
    return diffInMinutes < 15;
  }

  getHoursStringBetweenTimes(startDate: string, endDate: string) {
    const startHour = new Date(startDate).getHours();
    const endTime = new Date(endDate);
    const endHour =
      endTime.getMinutes() === 0 ? endTime.getHours() : endTime.getHours() + 1;

    const hourArray = [];
    for (let i = startHour; i <= endHour; i++) {
      hourArray.push(i + ':00');
    }
    return hourArray;
  }

  /**
   * Delegates to {@link TimelineChartService.getRelativePosition} — arithmetically
   * identical to the private copy this replaced, but it inherits that method's
   * zero-span guard instead of emitting an unparseable `calc()`.
   */
  getRelativePosition(time: Date | string, offSetInPixels: number): string {
    const sections = this.data().mainTimeline.timelineSections;

    return this.timelineChartService.getRelativePosition(
      time,
      offSetInPixels,
      new Date(sections.at(0)!.startTime!),
      this.getTimelineIntervalInMs(sections)
    );
  }

  getOffSetForTimeLabel(
    offSetInPixels: number,
    isFirst: boolean,
    isLast: boolean
  ): number {
    if (isFirst) {
      return 0;
    } else if (isLast) {
      return offSetInPixels * 2;
    } else {
      return offSetInPixels;
    }
  }

  toggle(key: string, newValue: boolean): void {
    this.amChartToggles.update((toggles: Map<string, boolean>) =>
      new Map(toggles).set(key, newValue)
    );
    const series = this.amChartSeries.get(key)!;

    const unit = this.data().amChart?.lines.find(
      (line: AmChartLine) => line.key === key
    )?.unit;

    series.states.applyAnimate(newValue ? 'highlighted' : 'faded');
    series.set(
      'tooltip',
      !newValue
        ? undefined
        : am5.Tooltip.new(this.root, {
            pointerOrientation: 'horizontal',
            labelText: '{valueY}' + (unit ? ' ' + unit : ''),
            keepTargetHover: true,
          })
    );
    if (newValue) {
      series.get('tooltip')!.events.on('dataitemchanged', this.emitDataContext);
    }
  }

  addTemporaryHighlight(hoveredKey: string): void {
    this.amChartSeries.forEach((series: am5xy.XYSeries, key: string) => {
      series.states.applyAnimate(key === hoveredKey ? 'highlighted' : 'faded');
    });
  }

  removeTemporaryHighlight(): void {
    this.amChartSeries.forEach((series: am5xy.XYSeries, key: string) => {
      series.states.applyAnimate(
        this.amChartToggles().get(key) ? 'highlighted' : 'faded'
      );
    });
  }

  // Run the function only in the browser. It used to also wrap `f()` in
  // `NgZone.runOutsideAngular` so amCharts' Animated-theme tickers could not drive
  // change detection; the app is zoneless, so no timer drives CD in the first place
  // and `NgZone` resolves to `NoopNgZone`, whose `runOutsideAngular` is `f()`.
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      f();
    }
  }

  createAmChart(): void {
    const root = am5.Root.new('chartdiv');
    root.setThemes([am5themes_Animated.new(root)]);
    root['_logo']!.dispose(); // hide the logo

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panY: false,
        layout: root.verticalLayout,
        height: am5.percent(100),
        paddingRight: 0,
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: -10,
      })
    );

    const data = this.data()
      .amChart!.data.map((item) => {
        return {
          ...item.values,
          position: item.position,
          date: new Date(item.date).getTime(),
        };
      })
      .sort((a, b) => a.date - b.date);

    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        groupData: false,
        baseInterval: {
          timeUnit: 'second',
          count: 1,
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 20,
          strokeOpacity: 0, // Hide the left and right borders
        }),
        width: am5.percent(100),
        min: data.at(0)!.date,
        max: data.at(data.length - 1)?.date,
        //max: data.at(data.length)?.date,

        //tooltip: am5.Tooltip.new(root, {}),
      })
    );

    // Hide vertical grid lines
    xAxis.get('renderer').grid.template.setAll({
      forceHidden: true,
    });

    // Hide labels on the date axis
    xAxis.get('renderer').labels.template.setAll({
      visible: false,
    });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: 20,
          strokeOpacity: 0, // Hide the left and right borders
        }),
        min: 0, // Set the base value for the y-axis
      })
    );

    // Hide labels on the value axis
    yAxis.get('renderer').labels.template.setAll({
      visible: false,
    });

    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        xAxis: xAxis,
        behavior: 'none',
      })
    );
    cursor.lineY.set('visible', false);

    const createSeries = (
      name: string,
      field: string,
      color: string,
      disabled: boolean,
      unit?: string
    ) => {
      const settingsWithoutTooltip: am5xy.ILineSeriesSettings = {
        name: name,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: field,
        valueXField: 'date',
        locationX: 0,
        stroke: am5.color(color),
        fill: am5.color(color),
        showTooltipOn: 'always',
        snapTooltip: true,
      };

      const settingsWithTooltip: am5xy.ILineSeriesSettings = {
        ...settingsWithoutTooltip,
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'horizontal',
          labelText: '{valueY}' + (unit ? ' ' + unit : ''),
          keepTargetHover: true,
        }),
      };

      const settings = disabled ? settingsWithoutTooltip : settingsWithTooltip;

      const series = chart.series.push(am5xy.LineSeries.new(root, settings));

      series.data.setAll(data);

      series.strokes.template.setAll({
        strokeWidth: 2,
        lineJoin: 'round', // Make the line ends rounded
      });

      const gradient = am5.LinearGradient.new(root, {
        stops: [color, '#FFFFFF'].map((color, index) => ({
          color: am5.color(color),
          offset: index,
        })),
      });

      series.fills.template.setAll({
        fillGradient: gradient,
        fillOpacity: 0.5,
        visible: true,
      });

      // Create highlighted state
      series.states.create('highlighted', {
        opacity: 1,
      });

      // Create faded state
      series.states.create('faded', {
        opacity: 0.2,
      });

      series.states.create('hidden', {
        opacity: 0,
      });

      if (disabled) {
        series.states.applyAnimate('hidden');
      }

      if (!disabled) {
        series
          .get('tooltip')!
          .events.on('dataitemchanged', this.emitDataContext);
      }

      this.amChartSeries.set(field, series);
    };

    this.data().amChart?.lines.forEach((line: AmChartLine) => {
      createSeries(
        line.title,
        line.key,
        line.color,
        !!line.disabled,
        line.unit
      );
    });

    chart.appear(1000, 100);
    this.root = root;
  }

  emitDataContext = (event: any) => {
    const dataItem = event.target.dataItem;
    if (dataItem) {
      this.amChartMouseOver.emit(dataItem?.dataContext as AmLineChartItem);
    }
  };

  getSectionWidthCssValue(ratio: number, gap: number | undefined): string {
    return `calc(${ratio}% - ${gap}px)`;
  }

  ngOnDestroy(): void {
    this.root?.dispose();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
